import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import SuccessModal, {
  useSuccess,
} from "../../../../../../../components/SuccessModal";
import AllFilePreview from "../../../../../../../components/ViewAllFile";
import StatusChip from "../../../../../../../components/StatusChip";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { useArchiveDocument } from "../../../../../../../queries/document/use-patient-document-archive";
import { useAssignToPatient } from "../../../../../../../queries/document/use-patient-document-assing";
import { usePatientDocumentList } from "../../../../../../../queries/document/use-patient-document-list";
import { GetAllDocumentsDocumentTagEnum } from "../../../../../../../services";
import { formatDateTime } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  PAGE_SIZE_OPTIONS,
  applyDefaultColumnSettings,
  filterActionCol,
  roleFilter,
  showToastOnApiCall,
} from "../../../../../../../utils/utils";
import UpsertUploadDocumentDrawer from "./UpsertUploadDocumentDrawer";
import {
  DOCUMENT_REV_MAPPER,
  DOCUMENT_TABLE_MAPPER,
  DOC_TYPE_MAPPER,
} from "./document-constant";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { getStoredRealm } from "@/utils/auth-storage";

interface PatientDocumentNewProps {
  tag?: GetAllDocumentsDocumentTagEnum;
  search?: string;
}

const PatientDocumentNew = ({ tag, search }: PatientDocumentNewProps) => {
  const { patientId } = useParams() as { patientId: any };
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [uploadDocDisclosure, viewDisclosure] = useReduceDisclosure(2);
  const deleteDocumentDisclosure = useDeleteDisClosure();
  const { mutateAsync: archiveDocument } = useArchiveDocument();
  const { mutateAsync: assignToPatient } = useAssignToPatient();
  const { hasReadOnly, hasUpdate, hasDelete, hasView } =
    useRoleController("Documents");

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const {
    data: patientDocuments,
    refetch,
    isFetching,
    isRefetching,
  } = usePatientDocumentList({
    documentTag: tag ?? undefined,
    patientId: patientId,
    pagination,
    gridSorting,
    searchString: search,
  });
  const [openedResendLink, { open: openResendLink, close: closeResendLink }] =
    useSuccess();

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      // {
      //   field: "srNo",
      //   headerName: "Sr.No",
      //   maxWidth: 70,
      //   renderCell: (params) =>
      //     params.api.getAllRowIds().indexOf(params.id) + 1,
      // },
      {
        field: "documentDescription",
        headerName: "Title",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "patientDocumentTags",
        headerName: "Tag",
        flex: 0.3,
        minWidth: 80,
        renderCell: ({ value }) => (
          <StatusChip
            label={DOCUMENT_TABLE_MAPPER[value] ?? "-"}
            variant="neutral"
          />
        ),
      },
      {
        field: "documentFormat",
        headerName: "Type",
        flex: 0.2,
        minWidth: 60,
        renderCell: ({ value }) => DOCUMENT_REV_MAPPER[value],
      },
      {
        field: "uploadedBy",
        headerName: "Uploaded By",
        flex: 0.6,
        minWidth: 120,
      },
      {
        field: "created",
        headerName: "Uploaded Date & Time",
        flex: 0.7,
        minWidth: 170,
        renderCell: ({ row }) =>
          row?.uploadedAt ? formatDateTime(row?.uploadedAt) : "-",
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: ACTION_LIST,
                compWith: {
                  view: hasView,
                  edit: hasUpdate,
                  assign: hasView,
                  delete: hasDelete,
                },
                removeOtherFields: hasReadOnly,
              })}
              onActionClick={(v) => {
                const transformedRow = {
                  file: row?.document,
                  documentTag: row?.patientDocumentTags,
                  document: undefined,
                  patientDocumentTags: undefined,
                  ...row,
                };

                if (v === "view") {
                  viewDisclosure.open(row);
                } else if (v === "edit")
                  uploadDocDisclosure.open(transformedRow);
                else if (v === "delete")
                  deleteDocumentDisclosure.open(row?.uuid);
                else if (v === "assign") handleAssignToPatient(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["created"]
  );

  const showSnackbar = useSnackbar();
  function handleAssignToPatient(id: any) {
    showToastOnApiCall({
      showSnackbar,
      apiCall: assignToPatient({
        patientDocumentId: id,
        xTENANTID: getStoredRealm() ?? undefined,
      }).then((res) => {
        openResendLink();
        return res;
      }),
    });
  }

  async function archiveHandler<T>(value: boolean, id: T) {
    const res = await archiveDocument({
      patientDocumentId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    });
    refetch();
    return res;
  }

  // const navigate = useNavigate();

  // const EndActionSection = () => {
  //   return (
  //     <Flex gap={3}>
  //       <Box width={"250px"} height={"40px"}>
  //         <CustomSelect
  //           value={tag as any}
  //           placeholder="Select Tag"
  //           options={DOCUMENT_TAGS}
  //           onChange={(v) => setTag(v as any)}
  //         />
  //       </Box>
  //       <Box style={{ height: "40px" }}>
  //         <StyledButtonV2
  //           style={{ width: "194px" }}
  //           label={"Upload Document"}
  //           onClick={() => uploadDocDisclosure.open()}
  //           variant="contained"
  //           shouldAddIcon
  //         />
  //       </Box>
  //     </Flex>
  //   );
  // };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <UpsertUploadDocumentDrawer disclosureHost={uploadDocDisclosure} />

      <SuccessModal
        label="Document Successfully Share To Patient"
        opened={openedResendLink}
        onClose={closeResendLink}
      />

      <AllFilePreview
        disclosureHost={viewDisclosure}
        file={viewDisclosure?.data?.document}
        fileType={
          DOC_TYPE_MAPPER[viewDisclosure.data?.documentFormat] ?? "other"
        }
        title="Preview Document"
        w="60vw"
      />

      <DeleteModal
        w="380px"
        open={deleteDocumentDisclosure.opened}
        onClose={deleteDocumentDisclosure.close}
        id={deleteDocumentDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Document"}
      />

      <CustomDataGridV2
        loading={isRefetching || isFetching}
        getRowId={(row) => row?.uuid}
        rows={patientDocuments?.data?.content ?? []}
        rowCount={patientDocuments?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, isVisitNotePage)}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={gridSorting}
        onSortModelChange={setGridSorting}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />
    </Box>
  );
};

export default PatientDocumentNew;

const ACTION_LIST = [
  { value: "view", label: "View" },
  { value: "assign", label: "Share To Patient" },
  { value: "edit", label: "Edit" },
  { value: "delete", label: "Delete" },
];
